<script lang="ts">
    import {Button, Modal, Select} from "flowbite-svelte";
    import type {Employee} from "$lib/types/employee";
    import EmailTemplateEditor from "$lib/components/EmailTemplateEditor.svelte";
    import {mailTemplates} from "$lib/stores/mailTemplates";
    import {getContext, onMount} from "svelte";
    import {getEmailTemplates, sendEmailTemplate} from "$lib/calls/mail";
    import {put, retire} from "$lib/api";
    import {findUnmatchedVariables} from "$lib/utils/templating/parseHandlebars";
    import {globalToast} from "$lib/stores/toast";
    import {CheckCircleSolid, EnvelopeSolid, PenSolid, TrashBinSolid} from "flowbite-svelte-icons";
    import type {EmailTemplate} from "$lib/types/EmailTemplate";
    import {getUnits} from "$lib/calls/unit";
    import dayjs from "dayjs";
    import {unitQualifications} from "$lib/calls/qualification";
    import {allUnits} from "$lib/stores/unit";
    import NewContractForm from "$lib/partials/employee/NewContractForm.svelte";
    import {qualifications} from "$lib/stores/qualifications";
    import hasEmploymentStatus from "$lib/utils/hasEmploymentStatus";
    import ModalFooter from "$lib/components/ui/ModalFooter.svelte";
    import {type Writable, writable} from "svelte/store";
    import BankAccount from "$items/employee/BankAccount.svelte";
    import EmployeeInfo from "$items/employee/EmployeeInfo.svelte";
    import Qualifications from "$items/employee/Qualifications.svelte";
    import TaxAndInsurance from "$items/employee/TaxAndInsurance.svelte";
    import {tariffs} from "$lib/stores/tariffs";
    import {getTariffs} from "$lib/calls/tariff";

    export let open: boolean = false;
    export let employee: Employee;


    let rerequestEmail = false;
    let mailContent: EmailTemplate;
    let mailTemplate: EmailTemplate;
    let submitting = false;
    let doneModal:Writable<boolean> = getContext('doneModal');

    let newContract = {
        id:null,
        contractType:null,
        history: null,
        currentHistory: {
            startDate: dayjs().add(1, 'month').date(1).format('YYYY-MM-DD'),
            endDate: dayjs().add(7, 'month').date(1).subtract(1, 'day').format('YYYY-MM-DD'),
        }
    };

    let formStatus = writable({
        loading: false,
        preview: null,
        formComplete: false,
        triggerPreview: false,
        triggerContract: false
    });

    let minTariffId:number|undefined;

    formStatus.subscribe(x => {
        if(x.formComplete) {
            mailTemplate =  $mailTemplates.find(x => x.level === 'contractible' && x.name.toLowerCase().includes('wiedereintritt'))
            console.log({mailTemplate})
        }
    })

    const abort = async () => {
        $formStatus.loading = true;
        if(newContract.id){
            await retire(`/employee/${employee.uuid}/contract/${newContract.id}?silent=true`);
        }
        $formStatus.preview = null;
        $formStatus.loading = false;
    }



    const updateStatus = async () => {
        employee.employmentStatus = 'data-re-provided';
        employee = await put('/employee/' + employee.uuid, employee)
    }
    const reRequestData = async () => {
        try{
            // update status
            employee.employmentStatus = 'data-re-requested';
            employee = await put('/employee/' + employee.uuid, employee)
            // send email
            await sendEmail();

            $globalToast.timed('Stammdaten angefordert', 4000, EnvelopeSolid)
            open = false
        } catch (e) {
            alert('Error: ' + e)
        }

    }
    const sendEmail = async () => {
        submitting = true;
        await sendEmailTemplate(mailTemplate.id, {
            fromEmail: 'legal@recrew.de',
            fromName: 'REcrew Legal',
            subject: newContract.id ? 'REcrew_Vertrag_zwischen_' + employee.name + ' und REcrew GmbH zur Signatur bereit!' : 'Bitte bestätige deine Stammdaten',
            entityId: employee.id,
            content: mailContent.content.replace(/<% body %>/g, ''),
            toEmail: employee.email,
            toName: employee.name,
            variables: findUnmatchedVariables(mailContent.content)
        })
        open = false;
        $doneModal = true;
    }




    onMount(async () => {
        if($mailTemplates.length === 0) {
            $mailTemplates = await getEmailTemplates();
        }
        if($allUnits.length === 0) {
            $allUnits = await getUnits();
        }
        if($tariffs.length === 0) {
            $tariffs = await getTariffs();
        }
        mailTemplate = $mailTemplates.find(x => x.level === 'candidate' && x.name.toLowerCase().includes('wiedereintritt'))
        if($qualifications.length === 0 && !$qualifications.find(x => x.unitId === employee.unitId)) {
            $qualifications = [...$qualifications,...await unitQualifications(employee.unitId)]
        }
        minTariffId = employee.contracts[0].history[0].tariffId
        employee.contracts.forEach(contract => {
            contract.history.forEach(history => {
                if($tariffs.find(x => x.id === history.tariffId).wagePerHour > $tariffs.find(x => x.id === minTariffId).wagePerHour){
                    minTariffId = history.tariffId
                }
            })
        })

    })


</script>

<Modal size="lg" title="Wiedereinstieg" bind:open={open}>

    {#if !hasEmploymentStatus(employee, 'data-re-provided') && !hasEmploymentStatus(employee, 'data-re-requested')}
        <p>Die Mitarbeiterstammdaten sind evtl. veraltet. </p>
        <p>Möchtest du beim Mitarbeiter eine Stammdatencheck anfordern?</p>
    {:else if hasEmploymentStatus(employee, 'data-re-requested')}
        <p>Der Mitarbeitende wurde aufgefordert die Stammdaten zu bestätigen.</p>
    {:else}
        <div class="mt-3 space-y-3">
            <EmployeeInfo display={false} readOnly/>
            <Qualifications display={false} readOnly/>
            <TaxAndInsurance display={false} readOnly/>
            <BankAccount display={false} readOnly/>
        </div>
<!--        <NewContractModal open {employee} on:done={() => {open = false; doneModal = true}}/>-->
        <NewContractForm bind:sharedState={formStatus} bind:contract={newContract} {employee} />

    {/if}

    {#if rerequestEmail}
        <form on:submit|preventDefault={reRequestData}>
            <EmailTemplateEditor on:update={e => mailContent = e.detail} preview={true} template={mailTemplate} fillData={{Employee: employee}}/>
            <!--<div class="flex mt-3 justify-end">
                <Button type="submit" >Stammdatenbestätigung anfordern</Button>
            </div>-->
        </form>

    {/if}
    {#if !submitting && newContract.id}
        <div class="space-y-2">
            <span>Mailvorlage</span>
            <Select on:change={()=> {submitting = true; setTimeout(()=> submitting = false, 50)}} required bind:value={mailTemplate} items={$mailTemplates.filter(x => x.level === 'contractible').map(x => ({...x, value: x}))}/>
        </div>
        <EmailTemplateEditor on:update={e => mailContent = e.detail} preview={true} template={mailTemplate} fillData={{Employee: employee, Contract: newContract.currentHistory, Unit: $allUnits.find(x => x.id === employee.unitId)}}/>

    {/if}
    <svelte:fragment slot="footer" >
        <ModalFooter disabled={submitting || $formStatus.loading} >
            <svelte:fragment slot="left">
                {#if !hasEmploymentStatus(employee, 'data-re-provided') && !hasEmploymentStatus(employee, 'data-re-requested')}
                    <Button type="button" outline on:click={() => updateStatus()} color="red" >Stammdatencheck überspringen</Button>
                {:else}
                    <Button disabled={$formStatus.loading} on:click={() => {abort(); open = false}} outline color="red" >
                        <TrashBinSolid class="w-4 h-4 me-2"/> <span>Abbrechen</span>
                    </Button>
                {/if}
                {#if !!$formStatus.preview}
                    <Button disabled={$formStatus.loading} outline on:click={() => abort()}>
                        <PenSolid class="w-4 h-4 me-2"/> Bearbeiten
                    </Button>
                {/if}
            </svelte:fragment>
            <svelte:fragment slot="right">
                {#if $formStatus.preview}
                    <Button disabled={$formStatus.loading} class="!text-decoration-none whitespace-nowrap" download="MAV_{employee.name.replaceAll(' ','_')}.docx" href={URL.createObjectURL($formStatus.preview)} target="_blank" color="light">Vorschau erzeugter Vertrag</Button>
                    {#if newContract.history}
                        <Button disabled={$formStatus.loading} on:click={() => sendEmail()} class="bg-sky-100 text-neutral-600 whitespace-nowrap">Email versenden</Button>
                    {:else}
                        <Button class="bg-sky-100 text-neutral-600 whitespace-nowrap" disabled={$formStatus.loading} on:click={() => $formStatus.triggerContract = true}>Vertrag erstellen</Button>
                    {/if}

                {:else if $formStatus.formComplete}
                    <Button disabled={$formStatus.loading} class="bg-sky-100 text-neutral-600 whitespace-nowrap" on:click={() => $formStatus.triggerPreview = true}>Vertragsvorschau erzeugen</Button>
                {/if}
                {#if !hasEmploymentStatus(employee, 'data-re-provided') && !hasEmploymentStatus(employee, 'data-re-requested')}
                    <Button type="button" disabled={submitting} class="bg-sky-100 text-neutral-600" on:click={() => rerequestEmail ? reRequestData() : rerequestEmail= true}>Stammdatencheck anfordern</Button>
                {/if}
            </svelte:fragment>


        </ModalFooter>
    </svelte:fragment>
</Modal>